/** Estate Management (Demo Mode only) - "control tower" landing dashboard for the Estate
 * Management workspace: overall estate position across all customers, contracts, site groups
 * and sites. No backend estate/maintenance feed exists yet - all figures are static mock data,
 * matching the same "seeded illustrative numbers" approach used by src/pages/FinanceDashboard.
 * Links into the existing real Store Portfolio settings pages (Instances/Markets/Stores)
 * rather than duplicating that data. */

import { useState } from 'react';
import { Box, Button, Chip, Grid, SvgIcon, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useTheme } from '@mui/material';
import { Store, Construction, CheckCircle, Warning, Assignment, FactCheck, Build, ReportProblem, PlaylistAddCheck } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import { TableHeader } from '../../components/forms/TableHeader';
import { Pill } from '../../components/shared/Pill';
import CustomPagination from '../../components/forms/Pagination';

const MOCK_TILES = [
  { key: 'totalSites', label: 'Total locations', value: '18', subtitle: 'Across all clients', icon: <Store />, color: '#1976d2' },
  { key: 'mobilising', label: 'Mobilising', value: '3', subtitle: 'Currently onboarding', icon: <Construction />, color: '#ed6c02' },
  { key: 'live', label: 'Live', value: '14', subtitle: 'Operating normally', icon: <CheckCircle />, color: '#2e7d32' },
  { key: 'sitesAtRisk', label: 'Locations at risk', value: '1', subtitle: 'Needs attention', icon: <Warning />, color: '#d32f2f' },
  { key: 'readinessGaps', label: 'Readiness gaps', value: '5', subtitle: 'Open readiness gaps', icon: <PlaylistAddCheck />, color: '#0288d1' },
  { key: 'openIssues', label: 'Open issues', value: '11', subtitle: 'Across the estate', icon: <Assignment />, color: '#9c27b0' },
  { key: 'complianceActionsDue', label: 'Compliance actions due', value: '4', subtitle: 'Due in next 30 days', icon: <FactCheck />, color: '#7b1fa2' },
  { key: 'criticalEquipmentDown', label: 'Critical equipment down', value: '2', subtitle: 'Awaiting repair', icon: <Build />, color: '#5d4037' },
  { key: 'sitesRequiringAttention', label: 'Locations requiring attention', value: '3', subtitle: 'Flagged for review', icon: <ReportProblem />, color: '#c2185b' },
];

const MOCK_MOBILISATION_READINESS = [
  { site: 'Riverside', customer: 'Northgate NHS Trust', stage: 'Mobilising', readiness: 'At risk', readinessPct: '45%', targetLaunch: '2026-07-20', blockingItems: 'Equipment install pending, supplier contract unsigned' },
  { site: 'Uptown', customer: 'Ridgeway Council', stage: 'Mobilising', readiness: 'On track', readinessPct: '78%', targetLaunch: '2026-07-25', blockingItems: 'Training completion' },
  { site: 'Southgate', customer: 'Fairview Holdings', stage: 'Mobilising', readiness: 'On track', readinessPct: '82%', targetLaunch: '2026-08-05', blockingItems: 'Menu sign-off' },
  { site: 'St Mary\u2019s School', customer: 'St Mary\u2019s Trust', stage: 'In development', readiness: 'Early stage', readinessPct: '20%', targetLaunch: '2026-09-01', blockingItems: 'Menu approval, DBS checks' },
  { site: 'Northgate Hospital', customer: 'Northgate NHS Trust', stage: 'In development', readiness: 'Early stage', readinessPct: '15%', targetLaunch: '2026-09-15', blockingItems: 'Site survey, labour plan' },
];

const MOCK_CUSTOMER_ESTATE = [
  { customer: 'St Mary\u2019s Trust', contract: 'Education Catering', sites: 6, live: 5, mobilising: 1, contractEnd: '2027-08-31' },
  { customer: 'Northgate NHS Trust', contract: 'Healthcare Catering', sites: 5, live: 4, mobilising: 0, contractEnd: '2026-12-31' },
  { customer: 'Ridgeway Council', contract: 'Corporate Dining', sites: 4, live: 3, mobilising: 1, contractEnd: '2028-03-31' },
  { customer: 'Fairview Holdings', contract: 'Retail Concessions', sites: 3, live: 2, mobilising: 1, contractEnd: '2027-01-31' },
];

const MOCK_OPERATIONAL_READINESS = [
  { area: 'Suppliers', status: 'Ready', sitesAffected: 0, notes: 'All primary suppliers onboarded' },
  { area: 'Menus', status: 'Gaps', sitesAffected: 2, notes: 'Menu sign-off outstanding at 2 sites' },
  { area: 'Allergens', status: 'Ready', sitesAffected: 0, notes: 'Allergen matrices complete' },
  { area: 'Labour', status: 'At risk', sitesAffected: 3, notes: 'Rota gaps ahead of launch' },
  { area: 'Training', status: 'Gaps', sitesAffected: 4, notes: 'Food safety refreshers overdue' },
  { area: 'Equipment', status: 'At risk', sitesAffected: 2, notes: 'Critical equipment installs delayed' },
  { area: 'Compliance', status: 'Gaps', sitesAffected: 4, notes: 'Compliance actions due within 30 days' },
];

const MOCK_OPEN_ISSUES = [
  { site: 'Riverside', category: 'Maintenance', issue: 'Walk-in freezer seal failure', priority: 'Critical', status: 'Open', raised: '2026-07-02' },
  { site: 'Downtown', category: 'Maintenance', issue: 'HVAC unit repair', priority: 'High', status: 'In Progress', raised: '2026-06-28' },
  { site: 'Uptown', category: 'Procurement', issue: 'Supplier contract unsigned', priority: 'High', status: 'Open', raised: '2026-07-05' },
  { site: 'St Mary\u2019s School', category: 'Mobilisation', issue: 'DBS checks outstanding for 2 staff', priority: 'Medium', status: 'In Progress', raised: '2026-07-01' },
  { site: 'Northgate Hospital', category: 'Compliance', issue: 'Fire risk assessment overdue', priority: 'Critical', status: 'Open', raised: '2026-06-30' },
  { site: 'Southgate', category: 'Maintenance', issue: 'Signage replacement', priority: 'Low', status: 'Open', raised: '2026-07-01' },
];

const MOCK_UPCOMING_DEADLINES = [
  { site: 'Riverside', type: 'Inspection', description: 'Statutory gas safety inspection', dueDate: '2026-07-15' },
  { site: 'Uptown', type: 'Launch', description: 'Go-live date', dueDate: '2026-07-25' },
  { site: 'St Mary\u2019s School', type: 'Audit', description: 'Pre-opening compliance audit', dueDate: '2026-08-10' },
  { site: 'Northgate NHS Trust', type: 'Client review', description: 'Quarterly contract review', dueDate: '2026-08-15' },
  { site: 'All sites', type: 'Training', description: 'Food safety refresher deadline', dueDate: '2026-08-01' },
];

const READINESS_COLOR: Record<string, 'success' | 'warning' | 'error'> = {
  'On track': 'success',
  'At risk': 'error',
  'Early stage': 'warning',
};

const STATUS_COLOR: Record<string, 'success' | 'warning' | 'error'> = {
  Ready: 'success',
  Gaps: 'warning',
  'At risk': 'error',
};

// Each operational readiness area links to the real index page that owns that concept.
const OPERATIONAL_READINESS_LINKS: Record<string, string> = {
  Suppliers: '/procurement/suppliers',
  Menus: '/menus',
  Allergens: '/range-planning/attributes-allergens',
  Labour: '/labour-management',
  Training: '/training/my-training',
  Equipment: '/estate-management/equipment',
  Compliance: '/estate-management/maintenance-compliance',
};

const EstateOverview = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const theme = useTheme();
  const [mobilisationPage, setMobilisationPage] = useState(0);
  const [customerPage, setCustomerPage] = useState(0);
  const [operationalPage, setOperationalPage] = useState(0);
  const [issuesPage, setIssuesPage] = useState(0);
  const [deadlinesPage, setDeadlinesPage] = useState(0);

  return (
    <PageArea>
      <PageTitle
        subTitle={t('estateManagementSubtitle', 'Monitor location readiness, mobilisation risk, equipment health and operational issues across the estate.')}>
        {t('estateManagementTitle', 'Estate Management')}
      </PageTitle>

      <Grid container
        spacing={4}
        sx={{ mb: 6, mt: 2 }}>
        {MOCK_TILES.map((tile) => (
          <Grid item
            xs={12}
            sm={6}
            md={4}
            key={tile.key}>
            <Section style={{ p: 4, mb: 0 }}>
              <Box display="flex"
                alignItems="center"
                justifyContent="space-between">
                <Box flex={1}>
                  <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
                    {t(`estateManagement${tile.key.charAt(0).toUpperCase()}${tile.key.slice(1)}`, tile.label)}
                  </Box>
                  <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                    {tile.value}
                  </Box>
                  <Box sx={{ fontSize: '12px', color: theme.palette.custom.gray[600] }}>
                    {tile.subtitle}
                  </Box>
                </Box>
                <Box sx={{ backgroundColor: `${tile.color}20`, borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color: tile.color }}>
                  <SvgIcon fontSize="medium">{tile.icon}</SvgIcon>
                </Box>
              </Box>
            </Section>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 6 }}>
        <Section>
          <TableHeader>
            {t('estateManagementMobilisationReadiness', 'Mobilisation readiness')}
            <Pill text={`${MOCK_MOBILISATION_READINESS.length} ${t('estateManagementSitesLabel', 'locations')}`} />
          </TableHeader>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('estateManagementStore', 'Location')}</TableCell>
                  <TableCell>{t('estateManagementCustomer', 'Client')}</TableCell>
                  <TableCell>{t('estateManagementStage', 'Stage')}</TableCell>
                  <TableCell>{t('estateManagementReadinessCategory', 'Readiness category')}</TableCell>
                  <TableCell>{t('estateManagementReadinessPct', 'Readiness')}</TableCell>
                  <TableCell>{t('estateManagementTargetLaunch', 'Target launch')}</TableCell>
                  <TableCell>{t('estateManagementBlockingItems', 'Blocking items')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {MOCK_MOBILISATION_READINESS.map((row, index) => (
                  <TableRow key={index}
                    hover
                    onClick={() => navigate('/customer-management/onboarding-pipeline')}
                    sx={{ cursor: 'pointer' }}>
                    <TableCell>{row.site}</TableCell>
                    <TableCell>{row.customer}</TableCell>
                    <TableCell>{row.stage}</TableCell>
                    <TableCell>
                      <Chip label={row.readiness}
                        size="small"
                        color={READINESS_COLOR[row.readiness] ?? 'default'}
                        variant="outlined" />
                    </TableCell>
                    <TableCell>{row.readinessPct}</TableCell>
                    <TableCell>{row.targetLaunch}</TableCell>
                    <TableCell>{row.blockingItems}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomPagination page={mobilisationPage}
            setPage={setMobilisationPage}
            maxPages={1}
            useNoDataMessage={false}
            breakpointForChangeDisplay={120000} />
        </Section>
      </Box>

      <Box sx={{ mt: 6 }}>
        <Section>
          <TableHeader>
            {t('estateManagementCustomerContractEstate', 'Client / contract estate')}
            <Pill text={`${MOCK_CUSTOMER_ESTATE.length} ${t('estateManagementContractsLabel', 'contracts')}`} />
          </TableHeader>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('estateManagementCustomer', 'Client')}</TableCell>
                  <TableCell>{t('estateManagementContract', 'Contract type')}</TableCell>
                  <TableCell>{t('estateManagementSiteCount', 'Sites')}</TableCell>
                  <TableCell>{t('estateManagementLive', 'Live')}</TableCell>
                  <TableCell>{t('estateManagementMobilising', 'Mobilising')}</TableCell>
                  <TableCell>{t('estateManagementContractEnd', 'Contract end')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {MOCK_CUSTOMER_ESTATE.map((row) => (
                  <TableRow key={row.customer}
                    hover
                    onClick={() => navigate('/customer-management/customers')}
                    sx={{ cursor: 'pointer' }}>
                    <TableCell>{row.customer}</TableCell>
                    <TableCell>{row.contract}</TableCell>
                    <TableCell>{row.sites}</TableCell>
                    <TableCell>{row.live}</TableCell>
                    <TableCell>{row.mobilising}</TableCell>
                    <TableCell>{row.contractEnd}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomPagination page={customerPage}
            setPage={setCustomerPage}
            maxPages={1}
            useNoDataMessage={false}
            breakpointForChangeDisplay={120000} />
        </Section>
      </Box>

      <Box sx={{ mt: 6 }}>
        <Section>
          <TableHeader>
            {t('estateManagementOperationalReadinessSummary', 'Operational readiness summary')}
            <Pill text={`${MOCK_OPERATIONAL_READINESS.length} ${t('estateManagementAreasLabel', 'areas')}`} />
          </TableHeader>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('estateManagementArea', 'Area')}</TableCell>
                  <TableCell>{t('estateManagementStatus', 'Status')}</TableCell>
                  <TableCell>{t('estateManagementSitesAffected', 'Sites affected')}</TableCell>
                  <TableCell>{t('estateManagementNotes', 'Notes')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {MOCK_OPERATIONAL_READINESS.map((row) => (
                  <TableRow key={row.area}
                    hover
                    onClick={() => navigate(OPERATIONAL_READINESS_LINKS[row.area] ?? '/estate-management/overview')}
                    sx={{ cursor: 'pointer' }}>
                    <TableCell>{row.area}</TableCell>
                    <TableCell>
                      <Chip label={row.status}
                        size="small"
                        color={STATUS_COLOR[row.status] ?? 'default'}
                        variant="outlined" />
                    </TableCell>
                    <TableCell>{row.sitesAffected}</TableCell>
                    <TableCell>{row.notes}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomPagination page={operationalPage}
            setPage={setOperationalPage}
            maxPages={1}
            useNoDataMessage={false}
            breakpointForChangeDisplay={120000} />
        </Section>
      </Box>

      <Box sx={{ mt: 6 }}>
        <Section>
          <TableHeader>
            {t('estateManagementOpenIssues', 'Open issues & service requests')}
            <Pill text={`${MOCK_OPEN_ISSUES.length} ${t('estateManagementIssuesLabel', 'issues')}`} />
            <Box sx={{ ml: 'auto' }}>
              <Button
                variant="secondary"
                onClick={() => navigate('/estate-management/service-requests')}
              >
                {t('estateManagementViewServiceRequests', 'View Service Requests')}
              </Button>
            </Box>
          </TableHeader>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('estateManagementStore', 'Location')}</TableCell>
                  <TableCell>{t('estateManagementCategory', 'Category')}</TableCell>
                  <TableCell>{t('estateManagementIssue', 'Issue')}</TableCell>
                  <TableCell>{t('estateManagementPriority', 'Priority')}</TableCell>
                  <TableCell>{t('estateManagementStatus', 'Status')}</TableCell>
                  <TableCell>{t('estateManagementRaised', 'Raised')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {MOCK_OPEN_ISSUES.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>{row.site}</TableCell>
                    <TableCell>{row.category}</TableCell>
                    <TableCell>{row.issue}</TableCell>
                    <TableCell>{row.priority}</TableCell>
                    <TableCell>{row.status}</TableCell>
                    <TableCell>{row.raised}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomPagination page={issuesPage}
            setPage={setIssuesPage}
            maxPages={1}
            useNoDataMessage={false}
            breakpointForChangeDisplay={120000} />
        </Section>
      </Box>

      <Box sx={{ mt: 6 }}>
        <Section>
          <TableHeader>
            {t('estateManagementUpcomingDeadlines', 'Upcoming deadlines')}
            <Pill text={`${MOCK_UPCOMING_DEADLINES.length} ${t('estateManagementItemsLabel', 'items')}`} />
            <Box sx={{ ml: 'auto' }}>
              <Button
                variant="secondary"
                onClick={() => navigate('/estate-management/maintenance-compliance')}
              >
                {t('estateManagementViewMaintenanceCompliance', 'View Maintenance & Compliance')}
              </Button>
            </Box>
          </TableHeader>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('estateManagementStore', 'Location')}</TableCell>
                  <TableCell>{t('estateManagementDeadlineType', 'Type')}</TableCell>
                  <TableCell>{t('estateManagementDescription', 'Description')}</TableCell>
                  <TableCell>{t('estateManagementDueDate', 'Due date')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {MOCK_UPCOMING_DEADLINES.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>{row.site}</TableCell>
                    <TableCell>
                      <Chip label={row.type}
                        size="small"
                        color="primary"
                        variant="outlined" />
                    </TableCell>
                    <TableCell>{row.description}</TableCell>
                    <TableCell>{row.dueDate}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomPagination page={deadlinesPage}
            setPage={setDeadlinesPage}
            maxPages={1}
            useNoDataMessage={false}
            breakpointForChangeDisplay={120000} />
        </Section>
      </Box>
    </PageArea>
  );
};

export default EstateOverview;
